import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { storage } from "../../utils";
import { playOrderNotificationChime } from "../../utils/orderNotificationChime";
import styles from "./OpsAlerts.module.css";

/**
 * Client-side alerts for NEW ordinary orders + shopping-list submissions.
 * Does NOT use quotation notification endpoints/IDs.
 *
 * Poll endpoints (read-only despite POST on get_orders — paginate/find only):
 *   POST https://dashboardapi.pipeup.ir/get_orders  { page: 1 }  sort creation_date:-1
 *   GET  https://dashboardapi.pipeup.ir/get_shoping_list
 *
 * Genuinely new = unseen _id AND creation_date > baselineAt (set after first seed).
 * Older unseen IDs are absorbed into seen without alerting (filter/pagination safety).
 * First successful poll never alerts (seeds current IDs).
 */

const CHANNEL = "pipeup-ops-alerts-v1";
const SEEN_KEY = "pipeup_ops_seen_v1";
const POLL_MS = 15000;
const API_BASE =
  process.env.REACT_APP_DASHBOARD_API_BASE || "https://dashboardapi.pipeup.ir";

function loadSeen() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : { orders: [], shop: [] };
    return {
      orders: new Set(parsed.orders || []),
      shop: new Set(parsed.shop || []),
    };
  } catch (_) {
    return { orders: new Set(), shop: new Set() };
  }
}

function saveSeen(seen) {
  try {
    localStorage.setItem(
      SEEN_KEY,
      JSON.stringify({
        orders: [...seen.orders],
        shop: [...seen.shop],
      })
    );
  } catch (_) {}
}

function createdMs(doc) {
  const raw = doc.creation_date || doc.createdAt || doc.created_at;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

async function authHeaders() {
  const token = await storage.load("token");
  if (!token) return null;
  return { "x-auth": token, "Content-Type": "application/json" };
}

async function fetchJson(url, opts) {
  const res = await fetch(url, opts);
  let json = {};
  try {
    json = await res.json();
  } catch (_) {}
  return { res, json };
}

const OpsAlerts = () => {
  const [soundOn, setSoundOn] = useState(false);
  const soundOnRef = useRef(false);
  const audioCtxRef = useRef(null);
  const seededRef = useRef(false);
  const baselineAtRef = useRef(0);
  const seenRef = useRef(loadSeen());
  const [toast, setToast] = useState(null);
  const [unread, setUnread] = useState({ orders: 0, shop: 0 });
  const [panelOpen, setPanelOpen] = useState(false);
  const [items, setItems] = useState([]);

  const playBeepTone = useCallback(() => {
    playOrderNotificationChime(audioCtxRef);
  }, []);

  const enableSound = useCallback(() => {
    soundOnRef.current = true;
    setSoundOn(true);
    playBeepTone();
  }, [playBeepTone]);

  const beep = useCallback(() => {
    if (!soundOnRef.current) return;
    playBeepTone();
  }, [playBeepTone]);

  const broadcastSeen = useCallback(() => {
    try {
      const bc = new BroadcastChannel(CHANNEL);
      bc.postMessage({
        type: "seen",
        payload: {
          orders: [...seenRef.current.orders],
          shop: [...seenRef.current.shop],
          baselineAt: baselineAtRef.current,
        },
      });
      bc.close();
    } catch (_) {}
    saveSeen(seenRef.current);
  }, []);

  const markAllSeen = useCallback(() => {
    items.forEach((it) => {
      if (it.kind === "order") seenRef.current.orders.add(it.id);
      else seenRef.current.shop.add(it.id);
    });
    setItems([]);
    setUnread({ orders: 0, shop: 0 });
    setToast(null);
    broadcastSeen();
  }, [items, broadcastSeen]);

  const markOneSeen = useCallback(
    (it) => {
      if (it.kind === "order") seenRef.current.orders.add(it.id);
      else seenRef.current.shop.add(it.id);
      setItems((prev) =>
        prev.filter((x) => !(x.kind === it.kind && x.id === it.id))
      );
      setUnread((u) => ({
        orders: it.kind === "order" ? Math.max(0, u.orders - 1) : u.orders,
        shop: it.kind === "shop" ? Math.max(0, u.shop - 1) : u.shop,
      }));
      broadcastSeen();
    },
    [broadcastSeen]
  );

  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel(CHANNEL);
      bc.onmessage = (ev) => {
        if (ev.data?.type === "seen") {
          seenRef.current.orders = new Set(ev.data.payload.orders || []);
          seenRef.current.shop = new Set(ev.data.payload.shop || []);
          if (ev.data.payload.baselineAt) {
            baselineAtRef.current = ev.data.payload.baselineAt;
          }
          saveSeen(seenRef.current);
        }
      };
    } catch (_) {}
    return () => {
      try {
        bc && bc.close();
      } catch (_) {}
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    const tick = async () => {
      try {
        const headers = await authHeaders();
        if (!headers) {
          // Logged out — stop polling
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          return;
        }

        const [{ res: oRes, json: oJson }, { res: sRes, json: sJson }] =
          await Promise.all([
            fetchJson(`${API_BASE}/get_orders`, {
              method: "POST",
              headers,
              body: JSON.stringify({ page: 1 }),
            }),
            fetchJson(`${API_BASE}/get_shoping_list`, {
              method: "GET",
              headers,
            }),
          ]);
        if (cancelled) return;

        const orderDocs = oRes.ok ? oJson.result?.docs || [] : [];
        const shopDocs = sRes.ok ? sJson.data || [] : [];

        if (!seededRef.current) {
          let maxCreated = 0;
          orderDocs.forEach((d) => {
            seenRef.current.orders.add(String(d._id));
            maxCreated = Math.max(maxCreated, createdMs(d));
          });
          shopDocs.forEach((d) => {
            seenRef.current.shop.add(String(d._id));
            maxCreated = Math.max(maxCreated, createdMs(d));
          });
          // Baseline = newest already-visible record (not wall clock), so
          // filter/pagination cannot surface older IDs as "new".
          baselineAtRef.current = maxCreated;
          seededRef.current = true;
          broadcastSeen();
          return; // never alert on initial load
        }

        const fresh = [];
        const baseline = baselineAtRef.current;

        for (const d of orderDocs) {
          const id = String(d._id);
          if (seenRef.current.orders.has(id)) continue;
          seenRef.current.orders.add(id);
          // Only alert if created after this session's baseline (genuinely new)
          if (createdMs(d) > baseline) {
            fresh.push({
              kind: "order",
              id,
              title: `سفارش جدید ${d.order_code || id.slice(-6)}`,
              href: `/dashboard/orderDetails/${id}`,
              at: d.creation_date || new Date().toISOString(),
            });
          }
        }
        for (const d of shopDocs) {
          const id = String(d._id);
          if (seenRef.current.shop.has(id)) continue;
          seenRef.current.shop.add(id);
          if (createdMs(d) > baseline) {
            fresh.push({
              kind: "shop",
              id,
              title: `لیست خرید جدید — ${d.name || "بدون نام"}`,
              href: `/dashboard/getShopingList`,
              at: d.creation_date || d.createdAt || new Date().toISOString(),
            });
          }
        }

        if (fresh.length) {
          broadcastSeen();
          setItems((prev) => [...fresh, ...prev].slice(0, 30));
          setUnread((u) => ({
            orders: u.orders + fresh.filter((f) => f.kind === "order").length,
            shop: u.shop + fresh.filter((f) => f.kind === "shop").length,
          }));
          setToast(fresh[0]);
          beep();
        } else {
          broadcastSeen();
        }
      } catch (_) {
        /* ignore poll errors */
      }
    };

    tick();
    intervalId = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [beep, broadcastSeen]);

  const totalUnread = unread.orders + unread.shop;

  return (
    <div className={styles.wrap} aria-live="polite">
      <div className={styles.bar}>
        <button
          type="button"
          className={styles.badgeBtn}
          onClick={() => setPanelOpen((v) => !v)}
        >
          اعلان سفارش / لیست خرید
          {totalUnread > 0 ? (
            <span className={styles.count}>{totalUnread}</span>
          ) : null}
        </button>
        <button
          type="button"
          className={soundOn ? styles.soundOn : styles.soundOff}
          onClick={enableSound}
        >
          {soundOn ? "صدای اعلان فعال" : "فعال‌سازی صدا"}
        </button>
        {totalUnread > 0 ? (
          <button type="button" className={styles.markBtn} onClick={markAllSeen}>
            علامت‌گذاری دیده‌شده
          </button>
        ) : null}
      </div>

      {toast ? (
        <div className={styles.toast} role="status">
          <strong>{toast.kind === "order" ? "سفارش" : "لیست خرید"}</strong>
          <span>{toast.title}</span>
          <Link to={toast.href} onClick={() => markOneSeen(toast)}>
            مشاهده
          </Link>
          <button type="button" onClick={() => setToast(null)}>
            بستن
          </button>
        </div>
      ) : null}

      {panelOpen ? (
        <div className={styles.panel}>
          {items.length === 0 ? (
            <p className={styles.empty}>اعلان جدیدی نیست.</p>
          ) : (
            <ul>
              {items.map((it) => (
                <li key={`${it.kind}-${it.id}`}>
                  <span className={styles.kind}>
                    {it.kind === "order" ? "سفارش" : "لیست خرید"}
                  </span>
                  <Link to={it.href} onClick={() => markOneSeen(it)}>
                    {it.title}
                  </Link>
                  <button type="button" onClick={() => markOneSeen(it)}>
                    دیده‌شد
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className={styles.note}>
            درخواست‌های قیمت اعلان جداگانه دارند و اینجا نمایش داده نمی‌شوند.
          </p>
        </div>
      ) : null}
    </div>
  );
};

export { OpsAlerts };
